import req from '@sportninja/common/api/request'
import { useCallback, useEffect, useState } from 'react'
import { Registration } from '@sportninja/common/types/Registration'
// One Division type for the whole registration flow — the complete one, which
// is a strict superset of the partial copy that used to live here.
import type { Division } from '@sportninja/common/types'

/**
 * A team already registered in this competition, as it arrives at the TOP LEVEL
 * of `GET /registration/:id` — each one carrying the division it belongs to.
 *
 * This is the list to read for anything team-first. The `teams` nested inside
 * each division is the same information seen from the other direction, and is
 * `null` rather than `[]` when empty.
 */
export interface RegistrationTeam {
  id: string
  /** The roster row this team registers through; sent on at `team_register`. */
  team_player_roster_id?: string | null
  name: string
  name_full: string | null
  abbreviation: string | null
  /** A bare URL, or null. */
  image: string | null
  /** A draft/bucket rather than a real team. */
  is_registration_bucket: boolean
  /**
   * UNLISTED, not restricted (Inon + Pouya, Sep 17). The team is hidden from the
   * picker; anyone holding `/registration/{root}?team_id={this id}` can still join.
   *
   * This list is deliberately NOT filtered server-side — the link resolves against
   * exactly these rows — so hiding a private team is the client's job. See
   * `visibleStartTeams`. Absent on an admin-attached team, which has no
   * registration row and is therefore never private.
   */
  is_private?: boolean
  division: Division
}

export interface RegistrationData {
  id: string
  name: string
  abbreviation: string
  image: string | null
  type: string
  sport_id: string
  registration: Registration
  divisions: Division[]
  /** Every registered team, each with its own division. May be absent. */
  teams?: RegistrationTeam[] | null
}

const GENERIC_ERROR_MESSAGE =
  'An error occurred while fetching registration data'

/**
 * Normalizes whatever `req` rejected with into an Error the UI can display.
 *
 * `req` throws the parsed JSON body directly for 4xx responses (see
 * `throwOnHTTPError` in `common/api/request.js`), so an API message like
 * "This competition could not be found." arrives as a plain object rather than
 * an Error — an `instanceof Error` check alone would discard it.
 */
const toRegistrationError = (err: unknown): Error => {
  if (err instanceof Error) {
    return err
  }

  const apiMessage = (err as { message?: unknown } | null)?.message

  if (typeof apiMessage === 'string' && apiMessage.trim()) {
    return new Error(apiMessage.trim())
  }

  return new Error(GENERIC_ERROR_MESSAGE)
}

export const useRegistration = (registrationId?: string) => {
  const [data, setData] = useState<RegistrationData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!registrationId) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await req(`/registration/${registrationId}`)
      setData(response.data)
    } catch (err) {
      setError(toRegistrationError(err))
    } finally {
      setLoading(false)
    }
  }, [registrationId])

  useEffect(() => {
    if (registrationId) {
      fetchData()
    }
  }, [fetchData, registrationId])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}
